import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { Flex } from "@redotech/redo-web/flex";
import { PageForm, PageFormSave } from "@redotech/redo-web/page-form";
import { Text } from "@redotech/redo-web/text";
import { groupInput, input, InputProvider } from "@redotech/ui/form";
import { memo, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { RedoAdminClientContext } from "../../client/context";
import { getTeam, updateTeam } from "../../client/team";
import { LoadTeamContext, TeamContext } from "../team";
import * as styles from "./logistics.module.css";
import {
  LogisticsGeneralCard,
  logisticsGeneralDefault,
  logisticsGeneralForm,
} from "./logistics/general";
import { LogisticsProductSummary } from "./logistics/logistics-product-summary";
import { SettingCard } from "./returns-package-protection/v2/component/setting-card";
import { SettingSection } from "./returns-package-protection/v2/component/setting-section";
import { SettingSectionTabNav } from "./returns-package-protection/v2/component/setting-section-tab-nav";
import {
  redoManagedReturnsFormValueToTeam,
  RedoManagedReturnsSettingsCardV2,
  redoManagedReturnsSettingsDefault,
  redoManagedReturnsSettingsForm,
  redoManagedReturnsTeamToFormValue,
} from "./returns-package-protection/v2/redo-managed-returns-settings";
import { TabNavigationProvider } from "./returns-package-protection/v2/tab-navigation-provider";

const reFulfillmentForm = groupInput({
  enabled: input<boolean>(),
  revSharePercentage: input<number>(),
});

type ReFulfillmentValue = InputProvider.Value<typeof reFulfillmentForm>;

const reFulfillmentDefault: ReFulfillmentValue = {
  enabled: false,
  revSharePercentage: 50,
};

export const LogisticsPage = memo(function LogisticsPage() {
  const params = useParams();
  const teamId = params.teamId!;

  const team = useContext(TeamContext);
  const loadTeam = useRequiredContext(LoadTeamContext);

  const handleSave = useHandler(() => {
    loadTeam();
  });

  const value: LogisticsValue | undefined = useMemo(() => {
    if (!team) {
      return;
    }

    return {
      general: {
        marketplace: !!team.settings.recommerce?.marketplace,
        consignmentPercentage: team.settings.recommerce?.consignmentPercentage,
      },
      redoManagedReturnsSettings: redoManagedReturnsTeamToFormValue(team),
      reFulfillment: {
        enabled: !!team.settings.reFulfillment?.enabled,
        revSharePercentage:
          team.settings.reFulfillment?.revSharePercentage ?? 50,
      },
    };
  }, [team]);

  return value ? (
    <LogisticsForm id={teamId} onSave={handleSave} value={value} />
  ) : null;
});

const logisticsForm = groupInput(
  {
    general: logisticsGeneralForm,
    redoManagedReturnsSettings: redoManagedReturnsSettingsForm,
    reFulfillment: reFulfillmentForm,
  },
  {
    validator: (value) => {
      if (
        value.redoManagedReturnsSettings.enabled &&
        !value.redoManagedReturnsSettings.defaultReturnsCenterId
      ) {
        return [
          "A default returns center must be selected when Redo Managed Returns is enabled",
        ];
      }
      return [];
    },
  },
);

type LogisticsValue = InputProvider.Value<typeof logisticsForm>;

const logisticsDefault: LogisticsValue = {
  general: logisticsGeneralDefault,
  redoManagedReturnsSettings: redoManagedReturnsSettingsDefault,
  reFulfillment: reFulfillmentDefault,
};

const LogisticsForm = memo(function LogisticsForm({
  id,
  onSave,
  value,
}: {
  id: string;
  onSave: (value: LogisticsValue) => void;
  value: LogisticsValue | undefined;
}) {
  const client = useRequiredContext(RedoAdminClientContext);

  const input = useInput(logisticsForm, value || logisticsDefault);

  const save: PageFormSave = useHandler(async (signal) => {
    const team = await getTeam(client, { teamId: id, signal });
    const formValue = input.value;

    team.settings.recommerce = {
      ...team.settings.recommerce,
      marketplace: formValue.general.marketplace,
      consignmentPercentage: formValue.general.consignmentPercentage,
    };

    team.settings.redoManagedReturns = redoManagedReturnsFormValueToTeam(
      formValue.redoManagedReturnsSettings,
    );

    team.redoManagedReturnsPricePerOrder =
      formValue.redoManagedReturnsSettings.coverageShare || undefined;

    team.settings.reFulfillment = {
      ...team.settings.reFulfillment,
      enabled: formValue.reFulfillment.enabled,
      revSharePercentage: formValue.reFulfillment.revSharePercentage,
    };

    await updateTeam(client, { teamId: id, team, signal });

    onSave(formValue);
  });

  const { general, redoManagedReturnsSettings, reFulfillment } = input.inputs;

  const enabledProducts = useMemo(() => {
    return [
      general.value.marketplace,
      redoManagedReturnsSettings.value.enabled,
      reFulfillment.value.enabled,
    ].filter(Boolean);
  }, [
    general.value.marketplace,
    redoManagedReturnsSettings.value.enabled,
    reFulfillment.value.enabled,
  ]);

  return (
    <TabNavigationProvider>
      <div className={styles.pageContainer}>
        <SettingSectionTabNav />
        <div className={styles.settingsContent}>
          <PageForm
            initial={value || logisticsDefault}
            input={input}
            save={save}
          >
            <SettingSection description="" id="products" title="Products">
              <SettingCard>
                <Flex align="stretch" dir="column" gap="none">
                  <Flex align="stretch" dir="column" gap="none" p="xl">
                    <Text fontSize="sm" fontWeight="medium" textColor="primary">
                      {`${enabledProducts.length} ${enabledProducts.length === 1 ? "product" : "products"} enabled`}
                    </Text>
                  </Flex>
                </Flex>
                <Flex align="stretch" dir="column" gap="none">
                  <LogisticsProductSummary
                    description="List products on the marketplace"
                    productEnabled={general.value.marketplace}
                    setProductEnabled={general.inputs.marketplace.setValue}
                    title="Recommerce / Liquidation"
                  />
                  <LogisticsProductSummary
                    description="Charge merchants for managed returns processing"
                    productEnabled={redoManagedReturnsSettings.value.enabled}
                    setProductEnabled={
                      redoManagedReturnsSettings.inputs.enabled.setValue
                    }
                    title="Redo Managed Returns"
                  />
                  <LogisticsProductSummary
                    description="Enable re-fulfillment for returned items"
                    productEnabled={reFulfillment.value.enabled}
                    setProductEnabled={reFulfillment.inputs.enabled.setValue}
                    title="Re-fulfillment"
                  />
                </Flex>
              </SettingCard>
            </SettingSection>
            <SettingSection
              description="Configure recommerce and liquidation settings for returned items"
              id="recommerce-liquidation"
              title="Recommerce / Liquidation"
            >
              <LogisticsGeneralCard input={general} />
            </SettingSection>
            <SettingSection
              description="Configure Redo managed returns processing settings"
              id="redo-managed-returns"
              title="Redo Managed Returns"
            >
              <RedoManagedReturnsSettingsCardV2
                input={redoManagedReturnsSettings}
              />
              {input.allErrors.length > 0 && (
                <Text fontSize="sm" textColor="error">
                  {input.allErrors[0]}
                </Text>
              )}
            </SettingSection>
            <SettingSection
              description="Configure re-fulfillment billing settings for returned items fulfilled from Returns Centers"
              id="re-fulfillment"
              title="Re-fulfillment"
            >
              <RedoIncrementDecrement
                description="Percentage of fulfilled order value charged as Redo's revenue share"
                label="Revenue Share Percentage"
                max={100}
                min={0}
                setValue={(value) =>
                  reFulfillment.inputs.revSharePercentage.setValue(value)
                }
                suffix="%"
                value={reFulfillment.value.revSharePercentage}
              />
            </SettingSection>
          </PageForm>
        </div>
      </div>
    </TabNavigationProvider>
  );
});
